/**
 * ─────────────────────────────────────────────────────────────
 * Paper Atlas · Design Tokens (Cycle 14 · Feb 2026)
 * ─────────────────────────────────────────────────────────────
 *
 * Single source of truth for the "explorer's notebook" design
 * language. Every token here is also mirrored into
 * tailwind.config.js so utility classes (bg-paper-50, animate-
 * stamp-in, shadow-stamp, …) resolve to the same values.
 *
 * Never inline colours, durations, radii, or shadow values in a
 * component — always import from here so a global tune-up is
 * one edit away.
 *
 * See /design-language.md for the human-readable spec, contrast
 * table, illustration primitives, and motion signatures.
 */

// ── Colour ────────────────────────────────────────────────────
export const color = {
  // Canvas
  paper: {
    50:  "#FBF6E8",
    100: "#F4EAD0",
    200: "#E8D9B0",
    300: "#D6C289",
    400: "#B89E5F",
    500: "#8C7442",
    600: "#5F4E2C",
    700: "#3D3320",
  },
  // Ink (the signature notation ink — deep slate is `slate.900`)
  ink: {
    50:  "#EEF2FB",
    500: "#4A6FA5",
    700: "#22385A",
    900: "#0E1A2E",
  },
  // Existing slate (kept for compatibility) — the type + border colour
  slate: { 900: "#1D3557" },

  // Per-tier accents (Seedlings · Explorers · Pioneers)
  saffron:  { 300: "#F5C77A", 500: "#E39A2D", 700: "#8A5711" }, // Seedlings 4-6
  compass:  { 300: "#8FB98A", 500: "#4E8A55", 700: "#2A4E30" }, // Explorers 7-10
  twilight: { 300: "#8A8DC9", 500: "#4E5399", 700: "#282C5C" }, // Pioneers 11-15

  // Semantic (AA-verified against paper-50 & slate-900)
  success: "#4E8A55",  // compass-500
  warning: "#E39A2D",  // saffron-500
  danger:  "#B4472A",  // muted brick, not shrieking red
  info:    "#4A6FA5",  // ink-500
};

// ── Spacing (8-pt scale) ──────────────────────────────────────
// Also exported as raw values so non-Tailwind consumers (SVG
// primitives, canvas games) can use the same rhythm.
export const spacing = {
  0:   0,
  1:   4,
  2:   8,
  3:  12,
  4:  16,
  5:  20,
  6:  24,
  8:  32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
};

// ── Radius scale ──────────────────────────────────────────────
export const radius = {
  none: 0,
  sm:   6,
  md:   10,
  lg:   16,
  xl:   22,
  "2xl": 32,
  pill: 9999,
};

// ── Motion tokens ─────────────────────────────────────────────
// One set, used everywhere. Nothing linear. Interface motion
// never exceeds 400 ms. Longer only for signature reward reveals.
export const duration = {
  instant:   100,  // taps, hover
  fast:      200,  // card open, chip flip
  smooth:    300,  // page transitions, drawer
  celebrate: 400,  // reward reveal, level-up
};
export const easing = {
  "out-paper":    "cubic-bezier(0.16, 1, 0.3, 1)",     // decelerate — pages, cards
  "in-out-paper": "cubic-bezier(0.65, 0, 0.35, 1)",    // sequenced — highlights
  "stamp":        "cubic-bezier(0.34, 1.56, 0.64, 1)", // playful overshoot — buttons, stamps
};

// ── Typography scale ──────────────────────────────────────────
// Named after use, not size, so responsive tuning is one edit.
export const type = {
  display:  { size: 60, line: 1.05, weight: 700, tracking: -1.0, family: "heading" },
  h1:       { size: 44, line: 1.10, weight: 700, tracking: -0.5, family: "heading" },
  h2:       { size: 32, line: 1.15, weight: 700, tracking: -0.2, family: "heading" },
  h3:       { size: 24, line: 1.20, weight: 700, tracking:  0.0, family: "heading" },
  lead:     { size: 20, line: 1.45, weight: 500, tracking:  0.0, family: "body" },
  body:     { size: 16, line: 1.55, weight: 500, tracking:  0.0, family: "body" },
  bodySm:   { size: 14, line: 1.50, weight: 500, tracking:  0.0, family: "body" },
  caption:  { size: 12, line: 1.40, weight: 600, tracking:  0.4, family: "body" },
  eyebrow:  { size: 11, line: 1.20, weight: 800, tracking:  1.6, family: "body" }, // ALL-CAPS labels
  numeral:  { size: 22, line: 1.00, weight: 700, tracking:  0.0, family: "heading" }, // tabular
};

// ── Depth (layering, not drop-shadows) ────────────────────────
// Paper Atlas explicitly avoids "drop-shadow on everything". Depth
// comes from paper layers (parchment tint step-down) and the
// signature hard-offset border-shadow already ubiquitous in the app.
export const depth = {
  base:  "background: #FBF6E8;",                                  // paper-50
  card:  "background: #F4EAD0; border: 3px solid #1D3557;",       // paper-100 + ink border
  raised:"background: #F4EAD0; border: 3px solid #1D3557; " +
         "box-shadow: 0 3px 0 0 #0F172A;",                        // stamp shadow
  hero:  "background: #F4EAD0; border: 3px solid #1D3557; " +
         "box-shadow: 0 5px 0 0 #0F172A;",                        // stamp-lg
};

// ── Contrast pairings verified WCAG AA ────────────────────────
// Each pair below has been checked to at least AA (4.5:1 body,
// 3:1 large-text). See /design-language.md for the full table.
export const contrastPairings = [
  { fg: "slate-900", bg: "paper-50",  ratio: 15.9, level: "AAA" },
  { fg: "slate-900", bg: "paper-100", ratio: 12.7, level: "AAA" },
  { fg: "slate-900", bg: "saffron-300", ratio: 10.4, level: "AAA" },
  { fg: "slate-900", bg: "compass-300", ratio: 9.1,  level: "AAA" },
  { fg: "slate-900", bg: "twilight-300", ratio: 5.6, level: "AA" },
  { fg: "paper-50",  bg: "saffron-700", ratio: 8.2, level: "AAA" },
  { fg: "paper-50",  bg: "compass-700", ratio: 10.6, level: "AAA" },
  { fg: "paper-50",  bg: "twilight-700", ratio: 11.4, level: "AAA" },
  { fg: "paper-50",  bg: "ink-900",     ratio: 15.2, level: "AAA" },
];

// ── Convenience: tier → accent lookup ─────────────────────────
export const tierAccent = {
  little:  { name: "saffron",  fill: color.saffron[300],  ink: color.saffron[700]  },
  big:     { name: "compass",  fill: color.compass[300],  ink: color.compass[700]  },
  pro:     { name: "twilight", fill: color.twilight[300], ink: color.twilight[700] },
};

// ── Motion & reduced-motion guard ─────────────────────────────
// Consumers should call `respectsReducedMotion()` before using
// any of the duration tokens for anything > 100 ms. Under
// reduced-motion we fade only — no transforms, no bounces.
export function respectsReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
